import { useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Save, X, KeyRound, Mail } from 'lucide-react';
import { api, type ApiError } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';

interface Judge {
  id: string;
  name: string;
  email: string;
  category: string;
  institution: string;
  experience: number;
}
type Form = { id?: string; name: string; email: string; category: string; institution: string; experience: string; passcode: string };
const blank: Form = { name: '', email: '', category: '', institution: '', experience: '0', passcode: '' };

export default function ManageJudges({ yearId, onChange }: { yearId: number; onChange: () => void }) {
  const [judges, setJudges] = useState<Judge[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<Form | null>(null);
  const [revealed, setRevealed] = useState<{ name: string; passcode: string } | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    const open = form !== null;
    if (open && !wasOpen.current) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    wasOpen.current = open;
  }, [form]);

  async function load() {
    setLoading(true);
    try {
      const d = await api<{ judges: Judge[] }>(`admin-judges?yearId=${yearId}`);
      setJudges(d.judges);
    } catch (e) {
      toast.error((e as ApiError).message || 'Failed to load judges');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, [yearId]);

  const set = (patch: Partial<Form>) => setForm((f) => (f ? { ...f, ...patch } : f));

  async function save() {
    if (!form) return;
    if (!form.name.trim()) return toast.error('Name is required');
    setBusy(true);
    try {
      const res = await api<{ judge: Judge; passcode?: string }>('admin-judges', {
        body: { action: form.id ? 'update' : 'create', yearId, ...form },
      });
      if (res.passcode) setRevealed({ name: res.judge.name, passcode: res.passcode });
      toast.success(form.id ? 'Judge updated' : 'Judge added');
      setForm(null);
      await load();
      onChange();
    } catch (e) {
      toast.error((e as ApiError).message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function remove(j: Judge) {
    if (!confirm(`Remove ${j.name}? This also removes their allocations and scores in this year.`)) return;
    try {
      await api('admin-judges', { body: { action: 'delete', id: j.id } });
      toast.success('Removed');
      await load();
      onChange();
    } catch (e) {
      toast.error((e as ApiError).message || 'Delete failed');
    }
  }

  return (
    <div className="space-y-4">
      {revealed && (
        <Card className="flex items-center gap-3 border-primary/40 bg-primary/5 p-4">
          <KeyRound className="size-5 text-primary" />
          <div className="flex-1 text-sm">
            Passcode for <span className="font-semibold">{revealed.name}</span>:{' '}
            <span className="rounded bg-card px-2 py-0.5 font-mono text-base font-bold tracking-widest">{revealed.passcode}</span>
            <span className="ml-2 text-muted-foreground">Copy it now — it won't be shown again.</span>
          </div>
          <Button size="icon" variant="ghost" onClick={() => setRevealed(null)} aria-label="Dismiss"><X /></Button>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{judges.length} judges</p>
        {!form && (
          <Button size="sm" onClick={() => setForm({ ...blank })}>
            <Plus /> Add judge
          </Button>
        )}
      </div>

      {form && (
        <Card ref={formRef} className="scroll-mt-20 space-y-3 p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{form.id ? 'Edit judge' : 'New judge'}</h3>
            <Button size="icon" variant="ghost" onClick={() => setForm(null)} aria-label="Close"><X /></Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Name"><Input value={form.name} onChange={(e) => set({ name: e.target.value })} /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="judge@example.com" /></Field>
            <Field label="Category"><Input value={form.category} onChange={(e) => set({ category: e.target.value })} placeholder="All Categories" /></Field>
            <Field label="Institution"><Input value={form.institution} onChange={(e) => set({ institution: e.target.value })} /></Field>
          </div>
          <Field label={form.id ? 'New passcode (leave blank to keep current)' : 'Passcode (leave blank to auto-generate)'}>
            <Input value={form.passcode} onChange={(e) => set({ passcode: e.target.value.toUpperCase() })} placeholder={form.id ? 'Unchanged' : 'Auto'} />
          </Field>
          <div className="flex gap-2">
            <Button onClick={save} disabled={busy}><Save /> {form.id ? 'Save changes' : 'Add judge'}</Button>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancel</Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full rounded-lg" />
          <Skeleton className="h-14 w-full rounded-lg" />
        </div>
      ) : (
        <div className="space-y-2">
          {judges.map((j) => (
            <Card key={j.id} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{j.name}</span>
                  {j.category && <Badge variant="secondary" className="hidden sm:inline-flex">{j.category}</Badge>}
                </div>
                {j.email && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Mail className="size-3" /> {j.email}
                  </div>
                )}
              </div>
              <Button size="icon" variant="ghost" onClick={() => setForm({ id: j.id, name: j.name, email: j.email, category: j.category, institution: j.institution, experience: String(j.experience), passcode: '' })} aria-label="Edit"><Pencil /></Button>
              <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(j)} aria-label="Delete"><Trash2 /></Button>
            </Card>
          ))}
          {judges.length === 0 && <p className="py-8 text-center text-muted-foreground">No judges yet. Add one, or clone a year's judges from the Years tab.</p>}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
